/* eslint-disable @typescript-eslint/naming-convention */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Auth } from 'aws-amplify';
import * as S3 from 'aws-sdk/clients/s3';
import { concat, defer, firstValueFrom, from, map, Observable, of, switchMap, zip } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { mergeMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { S3File } from '../types/s3-file.type';
function normalizeAllowedProjects(input: string[] | string | undefined): string[] {
  if (Array.isArray(input)) return input;
  if (typeof input === 'string') {
    return input
      .split(',')
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
  }
  return [];
}
function mapS3FolderToS3File(
  prefix: string,
  s3Folder: string,
  allowedProjects: string[] | string | undefined
): S3File {
  const list = normalizeAllowedProjects(allowedProjects);
  const hasAccess = prefix !== '' || list.some((project) => s3Folder.startsWith(project));
  return {
    key: s3Folder,
    name: s3Folder.replace(prefix, ''),
    isFolder: true,
    hasAccess,
  };
}
function mapS3ObjectToS3File(prefix: string, item: S3.Object): S3File {
  const s3File: S3File = {
    key: item.Key!,
    name: item.Key!.replace(prefix, ''),
    size: item.Size! / 1024,
    lastModified: item.LastModified!,
    isFolder: false,
    hasAccess: true, // when a file is visible it's always accessible in the UI
  };
  return s3File;
}
type AccessMethod = 'GET' | 'PUT' | 'DELETE' | 'POST';
const MAX_S3_ITEMS = 5001; // fetch one extra to detect if more than 5000 items exist

export type FolderRenameEvent =
  | { phase: 'gathering' }
  | { phase: 'moving'; current: number; total: number }
  | { phase: 'done' };

@Injectable({
  providedIn: 'root',
})
export class S3FileService {
  constructor(private httpClient: HttpClient) {}
  loadFilesAndFolders(
    bucket: string,
    prefix: string,
    allowedProjects: string[] | string | undefined
  ): Observable<{ files: S3File[]; wasTruncated: boolean }> {
    const auth: Promise<any> = Auth.currentCredentials();
    const region = 'eu-west-1';
    return from(auth).pipe(
      switchMap(async (creds: any) => {
        const s3 = new S3({ credentials: creds, region });
        let allPrefixes: S3.CommonPrefix[] = [];
        let allContents: S3.Object[] = [];
        let continuationToken: string | undefined = undefined;

        do {
          const res: S3.ListObjectsV2Output = await s3
            .listObjectsV2({
              Bucket: bucket,
              Prefix: prefix,
              Delimiter: '/',
              ContinuationToken: continuationToken,
            })
            .promise();

          allPrefixes = allPrefixes.concat(res.CommonPrefixes || []);
          allContents = allContents.concat(
            (res.Contents || []).filter((item) => item.Key !== prefix)
          );

          const total = allPrefixes.length + allContents.length;
          if (total >= MAX_S3_ITEMS) {
            break;
          }

          continuationToken = res.IsTruncated ? res.NextContinuationToken : undefined;
        } while (continuationToken);

        const folders = allPrefixes.map((item) =>
          mapS3FolderToS3File(prefix, item.Prefix!, allowedProjects)
        );
        const files = allContents.map((item) => mapS3ObjectToS3File(prefix, item));

        const allItems = [...folders, ...files];
        const wasTruncated = allItems.length >= MAX_S3_ITEMS;

        return { files: wasTruncated ? allItems.slice(0, MAX_S3_ITEMS - 1) : allItems, wasTruncated };
      })
    );
  }
  // Lists every object under a prefix (no Delimiter), recursing through all subfolders.
  listAllObjectsRecursive(bucket: string, prefix: string): Observable<S3.Object[]> {
    const auth: Promise<any> = Auth.currentCredentials();
    const region = 'eu-west-1';
    return from(auth).pipe(
      switchMap(async (creds: any) => {
        const s3 = new S3({ credentials: creds, region });
        let allContents: S3.Object[] = [];
        let continuationToken: string | undefined = undefined;

        do {
          const res: S3.ListObjectsV2Output = await s3
            .listObjectsV2({
              Bucket: bucket,
              Prefix: prefix,
              ContinuationToken: continuationToken,
            })
            .promise();

          allContents = allContents.concat(
            (res.Contents || []).filter((item) => item.Key !== prefix && !item.Key!.endsWith('/'))
          );

          continuationToken = res.IsTruncated ? res.NextContinuationToken : undefined;
        } while (continuationToken);

        return allContents;
      })
    );
  }
  uploadFiles(bucket: string, prefix: string, files: File[]): Observable<any> {
    const uploadConcurrency = 10;
    return from(files).pipe(
      // Limit concurrency so large batches remain reliable while uploading faster
      // than a strictly sequential queue.
      mergeMap((file) => defer(() => this.createPresignedUrl(
        bucket,
        prefix + file.name,
        'PUT',
        file.type
      ).pipe(
        switchMap((presignedUrl) => from(this.uploadWithRetry(presignedUrl, file)))
      )).pipe(
        // Continue with the remaining files when one file cannot be uploaded.
        catchError((error) => {
          console.error(`Upload failed for ${file.name}:`, error);
          return of(null);
        })
      ), uploadConcurrency)
    );
  }

  private async uploadWithRetry(url: string, file: File, maxAttempts = 4): Promise<Response> {
    let attempt = 0;
    while (true) {
      try {
        const response = await fetch(url, { method: 'PUT', body: file });
        if (!response.ok) {
          throw new Error(`Upload failed with status ${response.status}`);
        }
        return response;
      } catch (error) {
        attempt++;
        if (attempt >= maxAttempts) {
          throw error;
        }
        await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
      }
    }
  }
  private readonly DELETE_BATCH_SIZE = 20;

  deleteFilesByPrefix(bucket: string, prefix: string, onProgress?: (done: number, total: number) => void): Observable<{ succeeded: number; failed: string[] }> {
    const auth: Promise<any> = Auth.currentCredentials();
    return from(auth).pipe(
      switchMap(async (creds) => {
        const s3 = new S3({ credentials: creds });
        let allKeys: string[] = [];
        let continuationToken: string | undefined = undefined;
        do {
          const res: S3.ListObjectsV2Output = await s3.listObjectsV2({
            Bucket: bucket,
            Prefix: prefix,
            ContinuationToken: continuationToken,
          }).promise();
          allKeys = allKeys.concat((res.Contents || []).map((item: S3.Object) => item.Key!));
          continuationToken = res.IsTruncated ? res.NextContinuationToken : undefined;
        } while (continuationToken);
        return allKeys;
      }),
      switchMap((allKeys: string[]) => {
        if (allKeys.length === 0) return of({ succeeded: 0, failed: [] as string[] });
        return from(this.deleteBatched(bucket, allKeys, onProgress));
      })
    );
  }

  deleteExactKeys(bucket: string, keys: string[], onProgress?: (done: number, total: number) => void): Observable<{ succeeded: number; failed: string[] }> {
    if (keys.length === 0) return of({ succeeded: 0, failed: [] });
    return from(this.deleteBatched(bucket, keys, onProgress));
  }

  private async deleteBatched(bucket: string, keys: string[], onProgress?: (done: number, total: number) => void): Promise<{ succeeded: number; failed: string[] }> {
    let succeeded = 0;
    const failed: string[] = [];
    const total = keys.length;
    let processed = 0;
    for (let i = 0; i < keys.length; i += this.DELETE_BATCH_SIZE) {
      const batch = keys.slice(i, i + this.DELETE_BATCH_SIZE);
      let presignedUrls: string[];
      try {
        presignedUrls = await firstValueFrom(
          zip(batch.map((key: string) => this.createPresignedUrl(bucket, key, 'DELETE')))
        );
      } catch {
        failed.push(...batch);
        processed += batch.length;
        onProgress?.(processed, total);
        continue;
      }
      const results = await Promise.allSettled(
        presignedUrls.map((url: string) => fetch(url, { method: 'DELETE' }))
      );
      results.forEach((result, idx) => {
        if (result.status === 'fulfilled' && result.value.ok) {
          succeeded++;
        } else {
          failed.push(batch[idx]);
        }
      });
      processed += batch.length;
      onProgress?.(processed, total);
    }
    return { succeeded, failed };
  }
  // Paginates through all objects under a prefix and emits each resolved { blob, filename }
  // sequentially. The caller is responsible for triggering the browser download.
  downloadFilesByPrefix(bucket: string, prefix: string): Observable<{ blob: Blob; filename: string }> {
    return new Observable(subscriber => {
      let cancelled = false;

      (async () => {
        const creds = await Auth.currentCredentials();
        const s3 = new S3({ credentials: creds, region: 'eu-west-1' });

        const keys: string[] = [];
        let token: string | undefined;
        do {
          const res: S3.ListObjectsV2Output = await s3.listObjectsV2({
            Bucket: bucket,
            Prefix: prefix,
            ContinuationToken: token,
          }).promise();
          // skip folder placeholder objects (0-byte keys ending with /)
          (res.Contents ?? []).forEach(item => { if (item.Key && !item.Key.endsWith('/')) keys.push(item.Key); });
          token = res.IsTruncated ? res.NextContinuationToken : undefined;
        } while (token && !cancelled);

        for (let i = 0; i < keys.length && !cancelled; i++) {
          const key = keys[i];
          const blob = await firstValueFrom(this.downloadContent(bucket, key));
          subscriber.next({ blob, filename: key.split('/').slice(-1).join('') });
        }

        if (!cancelled) subscriber.complete();
      })().catch(err => subscriber.error(err));

      return () => { cancelled = true; };
    });
  }
  retrainFilesByPrefix(bucket: string, key: string): Observable<string> {
    return this.httpClient.post<string>(
      `${environment.restUrlSelfServiceAi}/retrain`,
      {
        method: 'POST',
        bucket: bucket,
        key: key,
      }
    );
  }

  renameFolderFiles(
    bucket: string,
    oldPrefix: string,
    newPrefix: string,
    excludeFilenames: string[] = []
  ): Observable<FolderRenameEvent> {
    return new Observable<FolderRenameEvent>(subscriber => {
      let cancelled = false;

      (async () => {
        subscriber.next({ phase: 'gathering' });

        const creds = await Auth.currentCredentials();
        const s3 = new S3({ credentials: creds, region: 'eu-west-1' });

        // Collect all object keys under oldPrefix (paginated)
        const keys: string[] = [];
        let token: string | undefined;
        do {
          const res = await s3.listObjectsV2({
            Bucket: bucket,
            Prefix: oldPrefix,
            ContinuationToken: token,
          }).promise();
          (res.Contents ?? []).forEach(item => { if (item.Key) keys.push(item.Key); });
          token = res.IsTruncated ? res.NextContinuationToken : undefined;
        } while (token && !cancelled);

        // Exclude files that must not be moved (e.g. train_configuration.toml, labeling_configuration.toml)
        const filteredKeys = excludeFilenames.length > 0
          ? keys.filter(k => !excludeFilenames.includes(k.split('/').pop() ?? ''))
          : keys;

        const total = filteredKeys.length;
        subscriber.next({ phase: 'moving', current: 0, total });

        for (let i = 0; i < filteredKeys.length && !cancelled; i++) {
          const oldKey = filteredKeys[i];
          const newKey = newPrefix + oldKey.slice(oldPrefix.length);

          await s3.copyObject({
            Bucket: bucket,
            CopySource: `${bucket}/${oldKey}`,
            Key: newKey,
          }).promise();

          // Direct SDK delete (consistent with copy/list above)
          await s3.deleteObject({ Bucket: bucket, Key: oldKey }).promise();
          // -- Presigned URL alternative (routes delete through backend Lambda) --
          // const url = await firstValueFrom(this.createPresignedUrl(bucket, oldKey, 'DELETE'));
          // await fetch(url, { method: 'DELETE' });

          subscriber.next({ phase: 'moving', current: i + 1, total });
        }

        if (!cancelled) {
          subscriber.next({ phase: 'done' });
          subscriber.complete();
        }
      })().catch(err => subscriber.error(err));

      return () => { cancelled = true; };
    });
  }

  renameFile(bucket: string, oldKey: string, newKey: string): Observable<'copying' | 'deleting' | 'done'> {
    const copy$ = from(Auth.currentCredentials()).pipe(
      switchMap((creds) => {
        const s3 = new S3({ credentials: creds, region: 'eu-west-1' });
        return from(
          s3.copyObject({
            Bucket: bucket,
            CopySource: `${bucket}/${oldKey}`,
            Key: newKey,
          }).promise()
        );
      }),
      map(() => 'deleting' as const)
    );

    // Direct SDK delete (consistent with copy above)
    const delete$ = from(Auth.currentCredentials()).pipe(
      switchMap((creds) => {
        const s3 = new S3({ credentials: creds, region: 'eu-west-1' });
        return from(s3.deleteObject({ Bucket: bucket, Key: oldKey }).promise());
      }),
      map(() => 'done' as const)
    );
    // -- Presigned URL alternative (routes delete through backend Lambda) --
    // const delete$ = this.createPresignedUrl(bucket, oldKey, 'DELETE').pipe(
    //   switchMap((url) => from(fetch(url, { method: 'DELETE' }))),
    //   map(() => 'done' as const)
    // );

    return concat(of('copying' as const), copy$, delete$);
  }

  async fileExists(bucket: string, key: string): Promise<boolean> {
    const creds = await Auth.currentCredentials();
    const s3 = new S3({ credentials: creds, region: 'eu-west-1' });
    try {
      await s3.headObject({ Bucket: bucket, Key: key }).promise();
      return true;
    } catch {
      return false;
    }
  }

  createSubdirectory(bucket: string, subdirKey: string): Observable<any> {
    // create presigned url by bucket + subdirKey:
    return this.createPresignedUrl(bucket, subdirKey, 'PUT').pipe(
      switchMap((presignedUrl) =>
        fetch(presignedUrl, { method: 'PUT', body: null })
      )
    );
  }
  downloadContent(bucket: string, objectKey: string): Observable<Blob> {
    return this.createPresignedUrl(bucket, objectKey, 'GET').pipe(
      switchMap((presignedUrl) => from(this.fetchWithResume(presignedUrl)))
    );
  }

  // Large downloads over slow/flaky connections (VPN, corporate proxies, etc.) can have their
  // TCP/TLS stream cut mid-transfer, which surfaces as a plain `net::ERR_FAILED` on the fetch even
  // though the presigned URL itself is valid (confirmed by pasting it directly into a new tab).
  // A single `fetch(...).blob()` has no retry/resume behaviour, so any mid-stream failure loses the
  // whole download. Instead, read the body as a stream and, on failure, re-request only the
  // remaining bytes via an HTTP Range header (supported by S3 without needing to re-sign the URL,
  // since Range is not part of the signed headers) so a dropped connection just resumes rather than
  // failing the entire download.
  private async fetchWithResume(url: string, maxAttempts = 4): Promise<Blob> {
    const chunks: Uint8Array[] = [];
    let received = 0;
    let totalSize: number | null = null;
    let attempt = 0;

    while (true) {
      try {
        const headers: Record<string, string> = received > 0 ? { Range: `bytes=${received}-` } : {};
        const response = await fetch(url, { method: 'GET', headers });
        if (!response.ok) {
          throw new Error(`Download failed with status ${response.status}`);
        }
        if (totalSize === null) {
          const contentRange = response.headers.get('Content-Range');
          const contentLength = response.headers.get('Content-Length');
          if (contentRange) {
            totalSize = Number(contentRange.split('/')[1]);
          } else if (contentLength) {
            totalSize = Number(contentLength);
          }
        }

        const reader = response.body!.getReader();
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          received += value.length;
        }

        if (totalSize === null || received >= totalSize) {
          return new Blob(chunks as BlobPart[]);
        }
        throw new Error('Connection closed before download completed');
      } catch (err) {
        attempt++;
        if (attempt >= maxAttempts) throw err;
        await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
      }
    }
  }
  // Returns a browser-loadable presigned GET URL (e.g. for use as an <img src>).
  getPresignedGetUrl(bucket: string, objectKey: string): Observable<string> {
    return this.createPresignedUrl(bucket, objectKey, 'GET');
  }
  private createPresignedUrl(
    bucket: string,
    objectKey: string,
    accessMethod: AccessMethod,
    contentType?: string
  ): Observable<string> {
    return this.httpClient.post<string>(
      `${environment.restUrlFeedbackData}/s3presigned`,
      {
        method: accessMethod,
        bucket: bucket,
        key: objectKey,
        contentType: contentType,
      }
    );
  }
}
